import {
  useQuery,
  type QueryClient,
  type QueryKey,
  type UnusedSkipTokenOptions
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  Artifact,
  Department,
  ExternalContact,
  OrgFunction,
  ShowProcessResponse,
  ShowProcessStepRecordingResponse,
  ShowSystemResponse,
  User
} from '@bliks-io/contracts';
import { notFoundOrUnknownKey } from '@/api/errors';
import {
  artifactKeys,
  departmentKeys,
  externalContactKeys,
  orgFunctionKeys,
  orgMemberKeys,
  processKeys,
  recordingKeys,
  systemKeys
} from '@/api/query-keys';
import { artifactQueryOptions } from '@/api/queries/useArtifacts';
import { departmentQueryOptions } from '@/api/queries/useDepartments';
import { externalContactQueryOptions } from '@/api/queries/useExternalContacts';
import { orgFunctionQueryOptions } from '@/api/queries/useOrgFunctions';
import { orgMemberQueryOptions } from '@/api/queries/useOrgMembers';
import { processQueryOptions } from '@/api/queries/useProcesses';
import { stepRecordingQueryOptions } from '@/api/queries/useRecording';
import { systemQueryOptions } from '@/api/queries/useSystems';

interface DetailQuery<TData, TKey extends QueryKey> {
  data: TData;
  key: TKey;
}

interface EntityDetailQueries {
  process: DetailQuery<ShowProcessResponse, ReturnType<typeof processKeys.detail>>;
  system: DetailQuery<ShowSystemResponse, ReturnType<typeof systemKeys.detail>>;
  orgMember: DetailQuery<User, ReturnType<typeof orgMemberKeys.detail>>;
  department: DetailQuery<Department, ReturnType<typeof departmentKeys.detail>>;
  artifact: DetailQuery<Artifact, ReturnType<typeof artifactKeys.detail>>;
  externalContact: DetailQuery<ExternalContact, ReturnType<typeof externalContactKeys.detail>>;
  function: DetailQuery<OrgFunction, ReturnType<typeof orgFunctionKeys.detail>>;
  team: DetailQuery<OrgFunction, ReturnType<typeof orgFunctionKeys.detail>>;
  stepRecording: DetailQuery<
    ShowProcessStepRecordingResponse,
    ReturnType<typeof recordingKeys.stepRecording>
  >;
}

export type BreadcrumbEntityKind = keyof EntityDetailQueries;

export interface BreadcrumbEntity {
  kind: BreadcrumbEntityKind;
  id: string;
}

/**
 * Loader data of the entity detail routes. These routes do not await the
 * entity — the request would hold the router in its pending state and hide the
 * page's own skeleton — so the loader hands a reference to the breadcrumb,
 * which resolves the name reactively from the query cache and translates its
 * own not-found fallback.
 */
export interface EntityBreadcrumbLoaderData {
  breadcrumbEntity: BreadcrumbEntity;
  breadcrumbSuffix?: string;
}

type EntityData<K extends BreadcrumbEntityKind> = EntityDetailQueries[K]['data'];

interface EntityKindConfig<K extends BreadcrumbEntityKind> {
  options: (
    id: string
  ) => UnusedSkipTokenOptions<EntityData<K>, Error, EntityData<K>, EntityDetailQueries[K]['key']>;
  /** The name, or for a step recording its process id; null while the recording is pending. */
  select: (data: EntityData<K>) => string | null;
  notFoundKey: string;
}

const orgFunctionKind = {
  options: orgFunctionQueryOptions,
  select: (orgFunction: OrgFunction) => orgFunction.name
};

const ENTITY_KINDS: { [K in BreadcrumbEntityKind]: EntityKindConfig<K> } = {
  process: {
    options: processQueryOptions,
    select: (response) => response.process.name,
    notFoundKey: 'processes.notFound'
  },
  system: {
    options: systemQueryOptions,
    select: (response) => response.data.name,
    notFoundKey: 'organization.systems.notFound'
  },
  orgMember: {
    options: orgMemberQueryOptions,
    select: (user) => `${user.firstName} ${user.lastName}`,
    notFoundKey: 'organization.members.notFound'
  },
  department: {
    options: departmentQueryOptions,
    select: (department) => department.name,
    notFoundKey: 'organization.departments.notFound'
  },
  artifact: {
    options: artifactQueryOptions,
    select: (artifact) => artifact.name,
    notFoundKey: 'organization.artifacts.notFound'
  },
  externalContact: {
    options: externalContactQueryOptions,
    select: (contact) => contact.name,
    notFoundKey: 'organization.contacts.notFound'
  },
  function: { ...orgFunctionKind, notFoundKey: 'organization.functions.notFound' },
  team: { ...orgFunctionKind, notFoundKey: 'organization.teams.notFound' },
  stepRecording: {
    options: stepRecordingQueryOptions,
    select: (response) =>
      'processStepRecording' in response ? response.processStepRecording.process : null,
    notFoundKey: 'errors.processStep.notFound'
  }
};

function entityNameQueryOptions<K extends BreadcrumbEntityKind>(kind: K, id: string) {
  const { options, select } = ENTITY_KINDS[kind];
  return { ...options(id), select };
}

export function createEntityBreadcrumbLoader<K extends BreadcrumbEntityKind, TParam extends string>(
  kind: K,
  idParam: TParam,
  breadcrumbSuffix?: string
) {
  return ({
    params,
    context
  }: {
    params: Record<TParam, string>;
    context: { queryClient: QueryClient };
  }): EntityBreadcrumbLoaderData => {
    const id = params[idParam];
    void context.queryClient.prefetchQuery(ENTITY_KINDS[kind].options(id));
    return { breadcrumbEntity: { kind, id }, breadcrumbSuffix };
  };
}

/**
 * Resolves the crumb label for an entity route match: the entity's name once its
 * shared detail query settles, a placeholder until then.
 */
export function useEntityBreadcrumbLabel(entity: BreadcrumbEntity | undefined) {
  const { t } = useTranslation();
  // Selecting the name keeps the header out of the re-render path of every
  // mutation that invalidates the (large) entity payload. Routes without an
  // entity crumb park a disabled observer on the empty id: it never fetches.
  // Both observers always run so the hook count stays fixed across routes.
  const kind = entity?.kind ?? 'process';
  const entityQuery = useQuery({
    ...entityNameQueryOptions(kind, entity?.id ?? ''),
    enabled: Boolean(entity)
  });
  const isStepRecording = kind === 'stepRecording';
  const recordingProcessId = isStepRecording ? entityQuery.data : undefined;
  const recordingProcessQuery = useQuery({
    ...entityNameQueryOptions('process', recordingProcessId ?? ''),
    enabled: Boolean(recordingProcessId)
  });

  // `??`, not `||`: an entity named '' has resolved, and reading it as
  // still-pending would leave the crumb loading forever. `null` is a recording
  // the invitee has not accepted yet: it carries no process.
  const resolve = (data: string | null | undefined, error: Error | null, notFoundKey: string) =>
    data === null
      ? '…'
      : (data ?? (error ? t(notFoundOrUnknownKey(error, notFoundKey)) : t('common.loading')));

  const label =
    isStepRecording && entityQuery.data
      ? resolve(
          recordingProcessQuery.data,
          recordingProcessQuery.error,
          ENTITY_KINDS.process.notFoundKey
        )
      : resolve(entityQuery.data, entityQuery.error, ENTITY_KINDS[kind].notFoundKey);

  // The suffix goes on every label, placeholders included: the parent route
  // renders the same entity without one, and two crumbs reading alike are
  // collapsed into one by HeaderBreadcrumb's consecutive-duplicate filter.
  return (suffix = '') => `${label}${suffix}`;
}
